
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../../components/feature/Sidebar';
import { TopBar } from '../../components/feature/TopBar';
import { TodayProduction } from './components/TodayProduction';
import { TodayConsumption } from './components/TodayConsumption';
import { GridExchange } from './components/GridExchange';
import { SelfConsumption } from './components/SelfConsumption';
import { DayCurveChart } from './components/DayCurveChart';

export default function EnergyTodayPage() {
  const navigate = useNavigate();
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [todayData, setTodayData] = useState<any>(null);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('is_authenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchTodayData = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 600));

        // Simular datos del día actual desde API Fronius
        const now = new Date();
        const currentHour = now.getHours();

        let totalProduced = 0;
        let totalConsumed = 0;
        let totalImported = 0;
        let totalExported = 0;
        let totalSelfConsumed = 0;

        // Calcular totales del día hasta la hora actual
        for (let hour = 6; hour <= Math.min(currentHour, 18); hour++) {
          const solarCurve = Math.sin(((hour - 6) / 12) * Math.PI);
          const produced = solarCurve * (2.5 + Math.random() * 0.5); // kWh
          const consumed = 1.2 + Math.random() * 0.8; // kWh

          totalProduced += produced;
          totalConsumed += consumed;

          const selfConsumed = Math.min(produced, consumed);
          totalSelfConsumed += selfConsumed;

          const excess = produced - consumed;
          if (excess > 0) {
            totalExported += excess;
          } else {
            totalImported += Math.abs(excess);
          }
        }

        setTodayData({
          production: {
            total: totalProduced,
            peak: 3.2,
            peakTime: '13:30',
            efficiency: 85
          },
          consumption: {
            total: totalConsumed,
            peak: 2.8,
            peakTime: '20:15',
            baseLoad: 0.8
          },
          grid: {
            imported: totalImported,
            exported: totalExported,
            balance: totalExported - totalImported,
            cost: totalImported * 0.15, // €/kWh
            revenue: totalExported * 0.08
          },
          selfConsumption: {
            total: totalSelfConsumed,
            percentage: (totalSelfConsumed / totalProduced) * 100,
            autonomy: (totalSelfConsumed / totalConsumed) * 100
          }
        });

        setLastUpdate(new Date());
      } catch (error) {
        console.error('Error al obtener datos del día:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTodayData();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Energía Hoy" lastUpdate={lastUpdate} />

        <div className="p-6 space-y-6">

          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl p-8">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Análisis Diario</h1>
                <p className="text-blue-100 max-w-xl">
                  Monitoreo detallado de la producción y consumo energético del día en curso.
                </p>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-100 bg-white/10 backdrop-blur-md px-4 py-2 rounded-xl border border-white/20">
                <i className="ri-calendar-check-line"></i>
                <span>{new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-96 bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Analizando datos del día...</p>
                <p className="text-gray-500 text-sm mt-2">Procesando curvas energéticas</p>
              </div>
            </div>
          ) : (
            <>
              {/* Resumen del día */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <TodayProduction data={todayData?.production} />
                <TodayConsumption data={todayData?.consumption} />
                <GridExchange data={todayData?.grid} />
                <SelfConsumption data={todayData?.selfConsumption} />
              </div>

              {/* Gráfica de curva del día */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                    <i className="ri-line-chart-line"></i>
                  </div>
                  Curva Energética del Día
                </h2>
                <DayCurveChart />
              </div>

              {/* Análisis y recomendaciones */}
              {todayData && (
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-8 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
                  <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>

                  <div className="relative z-10">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                        <i className="ri-brain-line text-white text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Análisis Inteligente</h3>
                        <p className="text-indigo-100 text-sm">Insights basados en tu comportamiento energético de hoy</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-white/10 backdrop-blur-md rounded-xl p-5 border border-white/10 hover:bg-white/20 transition-colors">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-2xl">📊</span>
                          <span className="font-bold text-white">Rendimiento</span>
                        </div>
                        <p className="text-indigo-100 text-sm leading-relaxed">
                          {todayData.production.efficiency > 80
                            ? `Excelente rendimiento del ${todayData.production.efficiency}%. El sistema está operando a máxima capacidad.`
                            : `Rendimiento del ${todayData.production.efficiency}%. Podría mejorar con limpieza de paneles.`
                          }
                        </p>
                      </div>

                      <div className="bg-white/10 backdrop-blur-md rounded-xl p-5 border border-white/10 hover:bg-white/20 transition-colors">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-2xl">💰</span>
                          <span className="font-bold text-white">Balance Económico</span>
                        </div>
                        <p className="text-indigo-100 text-sm leading-relaxed">
                          {todayData.grid.balance > 0
                            ? `Ganancia neta estimada de €${(todayData.grid.revenue - todayData.grid.cost).toFixed(2)} gracias a la exportación.`
                            : `Coste neto de €${(todayData.grid.cost - todayData.grid.revenue).toFixed(2)}. Intenta mover consumos a horas de sol.`
                          }
                        </p>
                      </div>

                      <div className="bg-white/10 backdrop-blur-md rounded-xl p-5 border border-white/10 hover:bg-white/20 transition-colors">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-2xl">🎯</span>
                          <span className="font-bold text-white">Optimización</span>
                        </div>
                        <p className="text-indigo-100 text-sm leading-relaxed">
                          {todayData.selfConsumption.percentage > 70
                            ? 'Autoconsumo óptimo alcanzado. ¡Estás aprovechando al máximo tu energía!'
                            : 'Oportunidad de mejorar autoconsumo. Programa electrodomésticos al mediodía.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
